// packages/web-ui/src/AppShell/useTabKeybindings.ts
//
// W2-S4 AC #6 (Task 7.4) — global tab keybindings:
//   Ctrl+W                — close active tab (Cmd+Shift+W on macOS)
//   Ctrl+Tab              — cycle to next tab in `tabOrder`
//   Ctrl+Shift+Tab        — cycle to previous tab in `tabOrder`
//
// Lives in a parallel hook (per Task 7.4 alternative wording) so the W2-S3
// `useDestinationKeybindings.ts` stays untouched — Pitfall #18 + parallel-
// agent rules. Both hooks attach independent `keydown` listeners; their key
// maps don't overlap (Ctrl+1..5 vs Ctrl+W/Tab) so there's no conflict.
//
// Source of truth: AC #6 of story 2-4.

import { useEffect } from 'react';

const isMac = (): boolean =>
  typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac');

/** Returns true when the keyboard event originated inside an editable element. */
function isInEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  if (target.matches('input, textarea, [contenteditable="true"]')) return true;
  return target.closest('input, textarea, [contenteditable="true"]') !== null;
}

export interface UseTabKeybindingsOptions {
  /** Closes the currently active tab; no-op if there is none. */
  closeActiveTab: () => void;
  /** Cycles to the next tab in `tabOrder`. */
  cycleNextTab: () => void;
  /** Cycles to the previous tab in `tabOrder`. */
  cyclePrevTab: () => void;
}

/**
 * Registers a global `keydown` listener for the three tab keybindings. The
 * caller must keep the option callbacks stable (the hook re-registers when
 * any reference changes — caller responsibility).
 *
 * Cross-platform Ctrl/Cmd:
 *   - On non-macOS: the standard binding is `Ctrl+W` and `Ctrl+Tab`.
 *   - On macOS: `Cmd+W` is reserved by the browser to close the window.
 *     Per AC #6 we use `Cmd+Shift+W` as the close-tab fallback. `Cmd+Tab`
 *     is reserved by the OS for app switching, so we ALSO accept `Ctrl+Tab`
 *     on macOS (matches every native browser tab UI).
 */
export function useTabKeybindings({
  closeActiveTab,
  cycleNextTab,
  cyclePrevTab,
}: UseTabKeybindingsOptions): void {
  useEffect(() => {
    function handler(event: KeyboardEvent): void {
      // Ctrl+Tab / Ctrl+Shift+Tab fire even when an input is focused (matches
      // every browser's native behaviour). Ctrl+W gets the same treatment;
      // we want to close the active tab even mid-typing per UX §2.2.
      const ctrlOrMeta = event.ctrlKey || event.metaKey;
      if (!ctrlOrMeta) return;

      // Ctrl+Tab → cycle next.
      if (event.key === 'Tab') {
        if (event.altKey) return; // Alt+Tab is reserved by the OS.
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          cyclePrevTab();
        } else {
          cycleNextTab();
        }
        return;
      }

      // Ctrl+W (or Cmd+Shift+W on macOS) → close active tab.
      if (event.key === 'w' || event.key === 'W') {
        if (event.altKey) return;
        if (isMac()) {
          // On macOS, Cmd+W closes the browser window. We require Shift to
          // disambiguate the SPA's "close tab" intent.
          if (event.metaKey && !event.shiftKey) return;
          // Plain Ctrl+W on macOS is non-standard but harmless — accept it.
        } else {
          // On non-macOS, Ctrl+Shift+W reopens-closed-tab in browsers; only
          // unshifted Ctrl+W maps to "close tab" here.
          if (event.shiftKey) return;
        }
        // For typing inside an editable target (input / textarea), avoid
        // closing the tab — the user is mid-edit. (Browser's own Ctrl+W
        // behaviour also defers to the focused input.)
        if (isInEditableTarget(event)) return;
        event.preventDefault();
        event.stopPropagation();
        closeActiveTab();
      }
    }

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [closeActiveTab, cycleNextTab, cyclePrevTab]);
}
